import { Controller } from "@hotwired/stimulus"
import { getRecaptchaToken } from "../lib/recaptcha"
import { applyPreferences } from "../lib/preferences"
import { onHeaderSignup } from "../lib/header_signup"

// Business tab: seven cards, one open at a time, each showing a working demo. Everything is
// fake data; the only things sent anywhere are the visitor's email, the pain points they
// ticked and, if they ask, a request for a walkthrough call.
const RING_CIRCUMFERENCE = 2 * Math.PI * 52

export default class extends Controller {
  static targets = [
    "card", "typePill", "quote",
    "profit", "profitArc", "profitMargin", "profitRevenue", "profitAmount", "profitSeg", "profitLegend",
    "discount", "discountPct", "readoutProfit", "readoutMargin",
    "chip", "planForm", "planEmail", "planError", "done"
  ]
  static values = { type: String, signedIn: Boolean, appUrl: String, recaptchaSiteKey: String }

  connect() {
    this.discountChanged()

    // The header's "Sign up free" button opens this modal while the Business tab is showing
    this.stopHeaderSignup = onHeaderSignup(this.element, () => this.openSignup())
  }

  disconnect() {
    this.stopHeaderSignup()
  }

  // ── Cards ──

  toggle(event) {
    const index = event.params.index
    this.open(this.isOpen(index) ? null : index)
  }

  next(event) {
    this.open(event.params.index + 1)
  }

  isOpen(index) {
    return !this.cardTargets[index].querySelector('.bw-card__panel').hidden
  }

  // Only one card is open at a time; opening one closes the last. Pass null to close all.
  open(index) {
    this.cardTargets.forEach((card, position) => {
      const open = position === index
      card.querySelector('.bw-card__panel').hidden = !open
      card.querySelector('.bw-card__head').setAttribute('aria-expanded', String(open))
      card.classList.toggle('bw-card--open', open)
    })

    if (index !== null) {
      requestAnimationFrame(() => this.cardTargets[index].scrollIntoView({ block: 'start', behavior: 'smooth' }))
    }
  }

  // ── Quote ──

  switchType(event) {
    this.typeValue = event.currentTarget.dataset.type
    this.quoteTargets.forEach(quote => { quote.hidden = quote.dataset.type !== this.typeValue })
    this.typePillTargets.forEach(pill => pill.setAttribute('aria-pressed', String(pill.dataset.type === this.typeValue)))
  }

  toggleComponents(event) {
    const button = event.currentTarget
    const parts = button.closest('.bw-line').querySelector('.bw-parts')
    parts.hidden = !parts.hidden

    button.setAttribute('aria-expanded', String(!parts.hidden))
    button.querySelector('.bw-line__toggle-label').textContent = parts.hidden ? 'Show components' : 'Hide components'
    button.querySelector('.bi').className = `bi ${parts.hidden ? 'bi-caret-down-fill' : 'bi-caret-up-fill'}`
  }

  // ── Profit ──

  discountChanged() {
    const revenue = Number(this.profitTarget.dataset.revenue)
    const costs = Number(this.profitTarget.dataset.costs)
    const discount = Number(this.discountTarget.value)

    const takings = revenue * (1 - discount / 100)
    const profit = takings - costs
    const margin = profit / takings

    this.setMoney(this.profitRevenueTarget, takings)
    this.setMoney(this.profitAmountTarget, profit)
    this.setMoney(this.profitLegendTarget, profit)
    this.setMoney(this.readoutProfitTarget, profit)
    this.profitMarginTarget.textContent = `${Math.round(margin * 100)}%`
    this.readoutMarginTarget.textContent = `${Math.round(margin * 100)}%`
    this.discountPctTarget.textContent = discount === 0 ? 'No discount' : `${discount}% off`
    this.profitSegTarget.style.flex = String(profit)
    this.profitArcTarget.setAttribute('stroke-dasharray', `${(RING_CIRCUMFERENCE * margin).toFixed(1)} ${RING_CIRCUMFERENCE.toFixed(1)}`)
  }

  setMoney(el, amount) {
    el.dataset.amount = String(Math.round(amount))
    applyPreferences(el.parentElement)
  }

  // ── Closing section: the plan by email, or a walkthrough call ──

  toggleChip(event) {
    const chip = event.currentTarget
    chip.classList.toggle('bw-chip--on')
    chip.setAttribute('aria-pressed', String(chip.classList.contains('bw-chip--on')))
  }

  sendPlan(event) {
    event.preventDefault()
    this.submitLead({
      subject: 'Business plan request',
      message: 'Asked for a plan from the Business page.',
      thanks: email => `Your plan is on its way to ${email}.`
    })
  }

  requestWalkthrough() {
    this.submitLead({
      subject: 'Business walkthrough request',
      message: 'Asked for a 30-minute walkthrough from the Business page.',
      thanks: email => `Thanks. We'll email ${email} to find a time for your 30-minute walkthrough.`
    })
  }

  async submitLead({ subject, message, thanks }) {
    if (!this.planEmailTarget.checkValidity()) return this.planEmailTarget.reportValidity()

    this.planErrorTarget.hidden = true
    const data = new FormData(this.planFormTarget)
    data.set('subject', subject)
    data.set('message', message)
    this.chipTargets
      .filter(chip => chip.classList.contains('bw-chip--on'))
      .forEach(chip => data.append('pain_points[]', chip.textContent.trim()))

    const token = await getRecaptchaToken(this.recaptchaSiteKeyValue, 'lead_capture')
    if (token) data.append('g-recaptcha-response', token)

    try {
      const response = await fetch(this.planFormTarget.action, {
        method: 'POST',
        body: data,
        headers: { 'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content, 'Accept': 'application/json' }
      })
      if (response.ok) return this.showThanks(thanks(this.planEmailTarget.value))

      this.showError((await response.json()).error || 'Something went wrong. Please try again.')
    } catch {
      this.showError('Network error. Please try again.')
    }
  }

  showThanks(text) {
    this.planFormTarget.hidden = true
    this.doneTarget.textContent = text
    this.doneTarget.hidden = false
  }

  showError(text) {
    this.planErrorTarget.textContent = text
    this.planErrorTarget.hidden = false
  }

  // ── Sign up ──

  openSignup() {
    // A signed-in visitor already has an account: send them to the app
    if (this.signedInValue) {
      window.location.href = this.appUrlValue
      return
    }

    const modal = this.element.querySelector('.signup-modal')
    this.application.getControllerForElementAndIdentifier(modal, 'signup-modal').open()
  }
}
